// src/components/Ownerdashboard/OwnerEditPropertyModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { updateOwnerProperty } from "./ownerAuth";
import {
  AGE_OPTIONS,
  BHK_OPTIONS,
  BROKERAGE_OPTIONS,
  FACING_OPTIONS,
  FURNISHED_OPTIONS,
  OCCUPANCY_OPTIONS,
  PARKING_OPTIONS,
  TENANT_OPTIONS,
  WASHROOM_TYPES,
  getUnitTypeOptions,
  withCurrentValue,
} from "../../constants/PropertyFieldDropdowns";

// Text fields the form edits directly; everything else needs custom handling.
// `title`, `area`, `type`, `address` and `city` are deliberately absent - they
// are shown read-only and are left out of the payload, so the server keeps its
// stored values. `propertyCurrentStatus` is edited from the property card.
const TEXT_FIELDS = ["floors", "description"];

const SELECT_FIELDS = [
  "unitType",
  "bhk",
  "furnishedType",
  "facing",
  "washroom",
  "parking",
  "brokerage",
  "occupencyType",
  "tenant",
  "age",
];

// Buyers read these off the description text rather than off separate fields,
// so picking one also writes it into the description.
const DESCRIPTION_TAGS = {
  washroom: "Washroom",
  parking: "Parking",
  facing: "Facing",
  age: "Age",
  tenant: "Tenant",
};

// The owner's free text always closes the description, so the generated
// segments have a fixed place to be written in front of.
const ADDITIONAL_LABEL = "Additional Description";
const ADDITIONAL_START = /(^|\/)\s*Additional Description\s*:/i;

// Existing descriptions write the separator inconsistently ("A / B/ C"),
// so they are split apart and rejoined in one shape.
const splitSegments = (text) =>
  String(text || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

/**
 * Splits a description into the generated `Label: value` segments and the
 * owner's own trailing text. The free text is sliced out rather than split on
 * "/", so slashes and spaces the owner types survive the round trip.
 */
const splitDescription = (description) => {
  const text = String(description || "");
  const match = text.match(ADDITIONAL_START);

  if (!match) return { details: splitSegments(text), additional: "" };

  return {
    details: splitSegments(text.slice(0, match.index)),
    // Drops only the single conventional space after the colon.
    additional: text.slice(match.index + match[0].length).replace(/^ /, ""),
  };
};

const joinDescription = (details, additional) =>
  [...details, ...(additional ? [`${ADDITIONAL_LABEL}: ${additional}`] : [])].join(" / ");

/**
 * Upserts a `Label: value` segment, so re-picking a value replaces the old
 * segment instead of stacking a new one and an empty value drops it. The
 * segment always lands before `Additional Description`.
 */
const applyDescriptionTag = (description, label, value) => {
  const { details, additional } = splitDescription(description);
  const isSameTag = new RegExp(`^${label}\\s*:`, "i");
  const kept = details.filter((segment) => !isSameTag.test(segment));

  if (value) kept.push(`${label}: ${value}`);

  return joinDescription(kept, additional);
};

const buildInitialForm = (property) => {
  const form = {};

  [...TEXT_FIELDS, ...SELECT_FIELDS].forEach((field) => {
    form[field] = property?.[field] ?? "";
  });

  // Numbers are held as strings so the inputs stay controlled while typing.
  form.rentValue = property?.rentValue === 0 || property?.rentValue ? String(property.rentValue) : "";
  form.sqFt = property?.sqFt === 0 || property?.sqFt ? String(property.sqFt) : "";

  return form;
};

// Reads a typed amount the way Indian listings say it out loud:
// 12000 -> "12 K", 120000 -> "1.2 Lac", 42000000 -> "4.2 Cr".
const formatShortAmount = (value) => {
  const amount = Number(value);
  if (value === "" || Number.isNaN(amount) || amount <= 0) return "";

  // Number() drops the trailing zeros toFixed leaves behind (1.20 -> 1.2).
  const trim = (n) => String(Number(n.toFixed(2)));

  if (amount >= 10000000) return `${trim(amount / 10000000)} Cr`;
  if (amount >= 100000) return `${trim(amount / 100000)} Lac`;
  if (amount >= 1000) return `${trim(amount / 1000)} K`;
  return trim(amount);
};

const Field = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    {children}
  </label>
);

const inputClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-[#503691] focus:outline-none focus:ring-1 focus:ring-[#503691] disabled:bg-gray-100";

const readOnlyClass =
  "w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500 cursor-not-allowed";

// `withCurrentValue` keeps a stored value selectable even when it is outside the
// canonical list, so opening the form never silently blanks an existing field.
const Select = ({ value, onChange, options, placeholder }) => (
  <select className={inputClass} value={value} onChange={onChange}>
    <option value="">{placeholder}</option>
    {withCurrentValue(options, value).map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </select>
);

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-[#503691]">
      <span className="h-4 w-1 rounded-full bg-[#503691]" />
      {title}
    </h3>
    {/* Two columns even on the narrowest phone - the short fields (BHK, price,
        sq ft...) pair up, and anything long opts out with `col-span-2`. */}
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">{children}</div>
  </div>
);

const OwnerEditPropertyModal = ({ property, onClose, onUpdated }) => {
  const [form, setForm] = useState(() => buildInitialForm(property));
  const [initialForm, setInitialForm] = useState(() => buildInitialForm(property));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const next = buildInitialForm(property);
    setForm(next);
    setInitialForm(next);
    setError("");
  }, [property]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const changedFields = useMemo(
    () => Object.keys(form).filter((key) => form[key] !== initialForm[key]),
    [form, initialForm]
  );

  const setField = (field) => (e) => {
    const { value } = e.target;

    setForm((prev) => {
      const next = { ...prev, [field]: value };
      const tag = DESCRIPTION_TAGS[field];

      if (tag) next.description = applyDescriptionTag(prev.description, tag, value);

      return next;
    });

    if (error) setError("");
  };

  // Type is read-only, so the unit type list follows the stored value.
  const unitTypeOptions = getUnitTypeOptions(property?.type);

  const priceShortForm = formatShortAmount(form.rentValue);

  // Only the trailing free text is editable; the generated segments in front of
  // it are owned by the dropdowns above.
  const { details: descriptionDetails, additional: additionalDescription } =
    splitDescription(form.description);

  const setAdditionalDescription = (e) => {
    const { value } = e.target;

    setForm((prev) => ({
      ...prev,
      description: joinDescription(splitDescription(prev.description).details, value),
    }));

    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.rentValue && Number.isNaN(Number(form.rentValue))) {
      setError("Price must be a number.");
      return;
    }
    if (form.sqFt && Number.isNaN(Number(form.sqFt))) {
      setError("Square feet must be a number.");
      return;
    }

    const payload = {
      PropertyObjectId: property?._id,
      PropertyId: property?.propertyId,
      ...TEXT_FIELDS.reduce((acc, field) => ({ ...acc, [field]: form[field]?.trim?.() ?? form[field] }), {}),
      ...SELECT_FIELDS.reduce((acc, field) => ({ ...acc, [field]: form[field] }), {}),
      rentValue: form.rentValue === "" ? null : Number(form.rentValue),
      sqFt: form.sqFt === "" ? null : Number(form.sqFt),
      images: Array.isArray(property?.images) ? property.images : [],
    };

    setSaving(true);
    try {
      const result = await updateOwnerProperty(payload);

      if (result.success) {
        toast.success(result.message);
        onUpdated?.(result);
        return;
      }

      setError(result.message);
      if (result.unauthorized) onUpdated?.(result);
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!property) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Edit Property</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              ID: {property?.propertyId || "NA"}
              {changedFields.length > 0 && (
                <span className="ml-2 rounded-full bg-[#503691]/10 px-2 py-0.5 font-semibold text-[#503691]">
                  {changedFields.length} change{changedFields.length === 1 ? "" : "s"}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-5">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-center text-sm font-medium text-red-600">
              {error}
            </div>
          )}

          <Section title="Basic Details">
            <Field label="Premise / Title (not editable)" className="col-span-2 lg:col-span-1">
              <input className={readOnlyClass} value={property?.title || ""} readOnly disabled />
            </Field>
            <Field label="Type (not editable)" className="col-span-2 sm:col-span-1">
              <input className={readOnlyClass} value={property?.type || ""} readOnly disabled />
            </Field>
            <Field label="Unit Type">
              <Select
                value={form.unitType}
                onChange={setField("unitType")}
                options={unitTypeOptions}
                placeholder="Select unit type"
              />
            </Field>
            <Field label="BHK">
              <Select
                value={form.bhk}
                onChange={setField("bhk")}
                options={BHK_OPTIONS}
                placeholder="Select BHK"
              />
            </Field>
          </Section>

          <Section title="Price & Size">
            <Field label={String(property?.type).includes("Rent") ? "Rent (₹)" : "Price (₹)"}>
              {/* The short form sits inside the field so the owner can sanity
                  check the zero count while typing. */}
              <div className="relative">
                <input
                  className={`${inputClass} pr-20`}
                  inputMode="numeric"
                  value={form.rentValue}
                  onChange={setField("rentValue")}
                  placeholder="e.g. 4500000"
                />
                {priceShortForm && (
                  <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs font-bold text-[#503691]">
                    {priceShortForm}
                  </span>
                )}
              </div>
            </Field>
            <Field label="Square Feet">
              <input
                className={inputClass}
                inputMode="numeric"
                value={form.sqFt}
                onChange={setField("sqFt")}
                placeholder="e.g. 1350"
              />
            </Field>
            <Field label="Brokerage">
              <Select
                value={form.brokerage}
                onChange={setField("brokerage")}
                options={BROKERAGE_OPTIONS}
                placeholder="Select brokerage"
              />
            </Field>
          </Section>

          <Section title="Location">
            <Field label="Address (not editable)" className="col-span-2 lg:col-span-3">
              <input className={readOnlyClass} value={property?.address || ""} readOnly disabled />
            </Field>
            <Field label="Area (not editable)">
              <input className={readOnlyClass} value={property?.area || ""} readOnly disabled />
            </Field>
            <Field label="City (not editable)">
              <input className={readOnlyClass} value={property?.city || ""} readOnly disabled />
            </Field>
          </Section>

          <Section title="Property Features">
            <Field label="Furnishing">
              <Select
                value={form.furnishedType}
                onChange={setField("furnishedType")}
                
                options={FURNISHED_OPTIONS}
                placeholder="Select furnishing"
              />
            </Field>
            <Field label="Facing">
              <Select
                value={form.facing}
                onChange={setField("facing")}
                options={FACING_OPTIONS}
                placeholder="Select facing"
              />
            </Field>
            <Field label="Washroom">
              <Select
                value={form.washroom}
                onChange={setField("washroom")}
                options={WASHROOM_TYPES}
                placeholder="Select washroom"
              />
            </Field>
            <Field label="Parking">
              <Select
                value={form.parking}
                onChange={setField("parking")}
                options={PARKING_OPTIONS}
                placeholder="Select parking"
              />
            </Field>
            <Field label="Floor">
              <input className={inputClass} value={form.floors} onChange={setField("floors")} />
            </Field>
            <Field label="Age">
              <Select
                value={form.age}
                onChange={setField("age")}
                options={AGE_OPTIONS}
                placeholder="Select age"
              />
            </Field>
            <Field label="Occupancy">
              <Select
                value={form.occupencyType}
                onChange={setField("occupencyType")}
                options={OCCUPANCY_OPTIONS}
                placeholder="Select occupancy"
              />
            </Field>
            <Field label="Tenant">
              <Select
                value={form.tenant}
                onChange={setField("tenant")}
                options={TENANT_OPTIONS}
                placeholder="Select tenant"
              />
            </Field>
          </Section>

          <Section title="Description">
            <Field label="Property Details (not editable)" className="col-span-2 lg:col-span-3">
              <div className={`${readOnlyClass} min-h-[42px] leading-relaxed`}>
                {descriptionDetails.length > 0
                  ? descriptionDetails.join(" / ")
                  : `Pick ${Object.values(DESCRIPTION_TAGS).join(", ")} above to fill this in`}
              </div>
              <span className="text-xs text-gray-400">
                Written from the {Object.values(DESCRIPTION_TAGS).join(", ")} dropdowns above -
                change a dropdown to change this line.
              </span>
            </Field>
            <Field label="Additional Description" className="col-span-2 lg:col-span-3">
              <textarea
                className={`${inputClass} min-h-[110px] resize-y`}
                value={additionalDescription}
                onChange={setAdditionalDescription}
                placeholder="Anything else buyers should know - e.g. Modular Kitchen, Fan, Wardrobe"
              />
            </Field>
          </Section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || changedFields.length === 0}
            className="rounded-full bg-[#503691] px-6 py-2 text-sm font-semibold text-white hover:bg-[#5b54e6] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Updating..." : "Confirm Update"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OwnerEditPropertyModal;
